import { useState, useEffect, useRef, useMemo } from "react";
import { LayoutGroup, useReducedMotion } from "framer-motion";
import type {
  InsightGroup as InsightGroupType,
  DataValues,
  NewNarrativeData,
  BookmarkData,
  NarrativeMetadata,
  ColorPalette,
} from "../types/narrative";
import { InsightItem } from "./InsightItem";
import { AnimationOverlays } from "./AnimationOverlays";
import { FlyingBookmark } from "./FlyingBookmark";
import { useBookmarkContext } from "../hooks/useBookmarkContext";
import type { AnimationStyle } from "./AnimatedText";

interface InsightGroupProps {
  group: InsightGroupType;
  data: DataValues;
  index: number;
  isVisible: boolean;
  onAnimationComplete: () => void;
  onReactionSelect?: () => void;
  sectionId: string;
  colorPalette?: ColorPalette; // Section's color palette for bookmark capture
  sectionAutoScroll?: boolean; // Section-level autoScroll default; group.autoScroll overrides when defined
  selectedSections?: Set<string>;
  onSectionToggle?: (sectionId: string, isSelected: boolean) => void;
  onContinue?: () => void;
  isNavigatingBackward?: boolean;
  narrativeData?: NewNarrativeData; // Optional - pass down for highlights_gallery
  metadata?: NarrativeMetadata; // Optional - for curator resolution in contentAuthor
  format?: string | null;
  noAnimation?: boolean; // When true, disable all animations for instant rendering (PDF export)
  onPlayWithMusic?: () => void; // Called when user chooses to play with music
  onMuteMusic?: () => void; // Called when user chooses to continue without music
}

// Seeded random to get consistent animation style per group
function getAnimationStyle(index: number): AnimationStyle {
  const styles: AnimationStyle[] = ["fade", "typewriter"];
  return styles[index % 2];
}

export function InsightGroup({
  group,
  data,
  index,
  isVisible,
  onAnimationComplete,
  onReactionSelect,
  sectionId,
  colorPalette,
  sectionAutoScroll,
  selectedSections,
  onSectionToggle,
  onContinue,
  isNavigatingBackward = false,
  narrativeData,
  metadata,
  format,
  noAnimation = false,
  onPlayWithMusic,
  onMuteMusic,
}: InsightGroupProps) {
  // Initialize to -1 so setting to 0 triggers a state change when isVisible starts as true
  const [currentItemIndex, setCurrentItemIndex] = useState(-1);
  const hasNotifiedComplete = useRef(false);
  const { setBookmark, isBookmarked } = useBookmarkContext();
  const [flyingBookmark, setFlyingBookmark] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const bookmarkButtonRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  // Generate unique groupId for tracking bookmarks
  const groupId = `${sectionId}-${index}`;
  const bookmarked = isBookmarked(groupId);

  // Use group's animationStyle if specified, otherwise determine based on group index
  const animationStyle = useMemo(
    () => group.animationStyle || getAnimationStyle(index),
    [group.animationStyle, index],
  );

  // Track if we're in the process of resetting (to avoid race condition)
  const isResettingRef = useRef(false);

  const prefersReducedMotion = useReducedMotion();

  // Skip sequential item reveal when:
  // 1. Navigating backward (show all items immediately)
  // 2. Group has animationStyle: "none" (no animation, no delay between items)
  // 3. noAnimation flag is set (PDF export mode)
  // 4. Reduced motion is requested — the staggered timed reveal is itself motion,
  //    and the per-item onComplete chain (e.g. a bar chart's MotionWrapper
  //    onAnimationComplete) is unreliable once MotionConfig neutralizes transforms,
  //    which would otherwise strand the chart and any items below it on re-entry.
  const skipSequentialReveal =
    isNavigatingBackward ||
    animationStyle === "none" ||
    noAnimation ||
    Boolean(prefersReducedMotion);

  // Effective autoScroll: group-level overrides section-level; disabled when reveal is skipped
  const autoScrollEnabled =
    (group.autoScroll ?? sectionAutoScroll ?? false) && !skipSequentialReveal;

  useEffect(() => {
    // Reset when visibility changes
    if (isVisible) {
      isResettingRef.current = true;
      // If skipping sequential reveal, immediately show all content
      setCurrentItemIndex(skipSequentialReveal ? group.items.length : 0);
      hasNotifiedComplete.current = false;
      // Reset scroll position so re-entering the group starts fresh
      if (scrollAreaRef.current) {
        scrollAreaRef.current.scrollTop = 0;
      }
      // Note: We don't reset bookmark state - it persists across navigation
    }
  }, [isVisible, skipSequentialReveal, group.items.length]);

  // Auto-scroll: keep newly revealed content in view as items animate in.
  // When the container's content is shorter than its viewport, scrollHeight === clientHeight
  // so scrollTo is a no-op and the natural vertical centering is preserved.
  useEffect(() => {
    if (!autoScrollEnabled || !isVisible || currentItemIndex <= 0) return;
    const el = scrollAreaRef.current;
    if (!el) return;
    const raf = requestAnimationFrame(() => {
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    });
    return () => cancelAnimationFrame(raf);
  }, [autoScrollEnabled, isVisible, currentItemIndex]);

  // Clear the resetting flag after state has been applied
  useEffect(() => {
    if (isVisible && currentItemIndex >= 0) {
      isResettingRef.current = false;
    }
  }, [isVisible, currentItemIndex]);

  useEffect(() => {
    // When all items have completed animation
    if (
      currentItemIndex >= group.items.length &&
      !hasNotifiedComplete.current &&
      isVisible &&
      !isResettingRef.current
    ) {
      hasNotifiedComplete.current = true;
      onAnimationComplete();
    }
  }, [currentItemIndex, group.items.length, isVisible, onAnimationComplete]);

  const handleItemComplete = () => {
    // Move to the next item
    setCurrentItemIndex((prev) => prev + 1);
  };

  const handleBookmarkClick = (snapshot: BookmarkData | null) => {
    const newBookmarkState = !bookmarked;
    setBookmark(groupId, snapshot);

    // Trigger flying bookmark animation when bookmarking
    if (newBookmarkState && bookmarkButtonRef.current) {
      const rect = bookmarkButtonRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      setFlyingBookmark({ x: centerX, y: centerY });
    }
    // Note: Auto-advance after bookmark removed - user should manually swipe to continue
  };

  // Check if this group contains a highlights_gallery item
  const hasHighlightsGallery = group.items.some(
    (item) => item.type === "highlights_gallery",
  );

  return (
    <div className="relative z-10 h-full w-full">
      {/* Group-level animation overlay(s) - separate layer, doesn't affect scroll */}
      {group.overlay && isVisible && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <AnimationOverlays
            overlay={group.overlay}
            keyPrefix={`group-${index}`}
          />
        </div>
      )}
      {/* Scrollable content area - only content affects scroll.
          Focusable (tabIndex) so keyboard users can scroll overflowing content
          (axe: scrollable-region-focusable); data-group-content lets the
          keyboard navigation hook find the current page's scroller. Only the
          visible group is in the tab order / exposed as a labeled group —
          off-screen groups are empty and should stay silent for screen readers. */}
      <div
        ref={scrollAreaRef}
        data-group-content={index}
        role={isVisible ? "group" : undefined}
        aria-label={isVisible ? `Page ${index + 1}` : undefined}
        tabIndex={isVisible ? 0 : -1}
        className="h-full w-full overflow-y-auto overflow-x-hidden focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-white/60"
      >
        {/* Centering wrapper - min-h-full allows content to push taller, flex centers when shorter */}
        <div className="min-h-full flex flex-col justify-center items-center px-8 py-24">
          <LayoutGroup>
            <div
              className={`w-full text-left ${hasHighlightsGallery ? "" : "max-w-sm xl:max-w-xl"}`}
            >
              {/* Items - wrapped in ref for bookmark snapshot.
                  data-group-items marks the rendered content block so the
                  continue indicator can tell whether anything is behind it. */}
              <div ref={contentRef} data-group-items className="flex flex-col">
                {isVisible &&
                  group.items.map((item, itemIndex) => {
                    const shouldRender =
                      skipSequentialReveal || itemIndex <= currentItemIndex;
                    const isCurrentItem =
                      !skipSequentialReveal && itemIndex === currentItemIndex;

                    return shouldRender ? (
                      <div
                        key={itemIndex}
                        style={{
                          marginTop:
                            itemIndex === 0
                              ? undefined
                              : (item.marginTop ?? "1.5rem"),
                        }}
                      >
                        <InsightItem
                          item={item}
                          data={data}
                          animationStyle={animationStyle}
                          animationSpeed={group.animationSpeed}
                          delay={isCurrentItem ? 0.5 : 0}
                          onComplete={
                            isCurrentItem ? handleItemComplete : () => {}
                          }
                          selectedSections={selectedSections}
                          onSectionToggle={onSectionToggle}
                          onContinue={onContinue}
                          onReactionSelect={onReactionSelect}
                          skipAnimation={skipSequentialReveal}
                          narrativeData={narrativeData}
                          metadata={metadata}
                          groupId={groupId}
                          onPlayWithMusic={onPlayWithMusic}
                          onMuteMusic={onMuteMusic}
                          // Bookmark-related props for cta_bookmark items
                          contentRef={contentRef}
                          sectionId={sectionId}
                          colorPalette={colorPalette}
                          isBookmarked={bookmarked}
                          onBookmarkClick={handleBookmarkClick}
                          bookmarkButtonRef={bookmarkButtonRef}
                          format={format}
                        />
                      </div>
                    ) : null;
                  })}
              </div>
            </div>
          </LayoutGroup>
        </div>
      </div>

      {/* Flying bookmark animation */}
      {flyingBookmark && (
        <FlyingBookmark
          startPosition={flyingBookmark}
          onComplete={() => setFlyingBookmark(null)}
        />
      )}
    </div>
  );
}
